import { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { CheckCircle, AlertCircle } from "lucide-react";
import { newsletterAPI, ApiError } from "../services/api";

export const Newsletter = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setError("Please enter your email address");
      return;
    }

    if (!/\S+@\S+\.\S+/.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await newsletterAPI.subscribe(email);
      
      if (response) {
        setIsSuccess(true);
        setEmail("");
      } else {
        setError(response || 'Subscription failed. Please try again.');
      }
    } catch (error) {
      const errorMessage = error instanceof ApiError 
        ? error.message 
        : 'Network error. Please try again.';
      setError(errorMessage);
      console.error('Newsletter subscription error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (value: string) => {
    setEmail(value);
    // Clear error when user starts typing
    if (error) {
      setError(null);
    }
    // Reset success state when user starts typing again
    if (isSuccess) {
      setIsSuccess(false);
    }
  };

  return (
    <section id="newsletter">
      <hr className="w-11/12 mx-auto" />

      <div className="container py-24 sm:py-32">
        <h3 className="text-center text-4xl md:text-5xl font-bold">
          Stay Ahead of{" "}
          <span className="bg-gradient-to-b from-primary/60 to-primary text-transparent bg-clip-text">
            The Markets
          </span>
        </h3>
        <p className="text-xl text-muted-foreground text-center mt-4 mb-8">
          Get exclusive platform and community updates delivered to your inbox.
        </p>

        <form
          className="flex flex-col w-full md:flex-row md:w-6/12 lg:w-4/12 mx-auto gap-4 md:gap-2"
          onSubmit={handleSubmit}
        >
          <div className="flex-1">
            <Input
              type="email"
              placeholder="trader@example.com"
              className="bg-muted/50 dark:bg-muted/80"
              aria-label="email"
              value={email}
              onChange={(e) => handleInputChange(e.target.value)}
              disabled={isLoading}
            />
          </div>
          <Button 
            type="submit" 
            disabled={isLoading || isSuccess}
            className="min-w-[120px]"
          >
            {isLoading ? "Subscribing..." : isSuccess ? "Subscribed!" : "Join Waitlist"}
          </Button>
        </form>

        {/* Success Message */}
        {isSuccess && (
          <div className="flex items-center justify-center gap-2 mt-4 text-green-600">
            <CheckCircle className="h-5 w-5" />
            <span>Thank you for subscribing! Check your email for confirmation.</span>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="flex items-center justify-center gap-2 mt-4 text-red-600">
            <AlertCircle className="h-5 w-5" />
            <span>{error}</span>
          </div>
        )}
      </div>

      <hr className="w-11/12 mx-auto" />
    </section>
  );
};
